import React, { useState } from 'react';
import styled from 'styled-components';
import { woocommerceService } from '../../../services/woocommerceService';

const TestContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TestButton = styled.button`
  background-color: #007cba;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  margin: 1rem 0;
  
  &:hover {
    background-color: #005a87;
  }
`;

const ResultContainer = styled.div`
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 0.9rem;
  max-height: 400px;
  overflow-y: auto;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border-left: 4px solid #c62828;
`;

const WooCommerceTest = () => {
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const testAPI = async () => {
    setLoading(true);
    setError('');
    setResult('Testing WooCommerce API connection...\n\n');
    
    try {
      // Test 1: Basic API connection
      setResult(prev => prev + 'Test 1: Testing basic API connection...\n');
      const products = await woocommerceService.getProducts({ per_page: 1 });
      setResult(prev => prev + `✅ Success! Found ${products.length} products\n\n`);
      
      // Test 2: Categories
      setResult(prev => prev + 'Test 2: Testing categories...\n');
      const categories = await woocommerceService.getCategories({ per_page: 5 });
      setResult(prev => prev + `✅ Success! Found ${categories.length} categories\n\n`);
      
      // Test 3: Single product (if we have products)
      if (products.length > 0) {
        setResult(prev => prev + `Test 3: Testing single product fetch (ID: ${products[0].id})...\n`);
        const singleProduct = await woocommerceService.getProduct(products[0].id);
        setResult(prev => prev + `✅ Success! Product: ${singleProduct.name}\n\n`);
      }
      
      setResult(prev => prev + '🎉 All tests passed! WooCommerce API is working correctly.\n');
      
    } catch (err) {
      setError(`❌ API Test Failed: ${err.message}`);
      setResult(prev => prev + `❌ Error: ${err.message}\n\n`);
      
      // Additional debugging info
      setResult(prev => prev + 'Debugging Information:\n');
      setResult(prev => prev + `- API URL: ${process.env.REACT_APP_WOOCOMMERCE_API_URL || 'http://localhost:8888/Website/duchessCollection/wp-json/wc/v3'}\n`);
      setResult(prev => prev + `- Consumer Key: ${process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY ? 'Set' : 'Not set'}\n`);
      setResult(prev => prev + `- Consumer Secret: ${process.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET ? 'Set' : 'Not set'}\n`);
      setResult(prev => prev + '\nTroubleshooting Steps:\n');
      setResult(prev => prev + '1. Check if WooCommerce is installed and activated\n');
      setResult(prev => prev + '2. Verify API credentials in WordPress admin\n');
      setResult(prev => prev + '3. Check API key permissions (should be Read/Write)\n');
      setResult(prev => prev + '4. Ensure the user has administrator role\n');
      setResult(prev => prev + '5. Check if WooCommerce REST API is enabled\n');
    } finally {
      setLoading(false);
    }
  };

  return (
    <TestContainer>
      <h2>WooCommerce API Test</h2>
      <p>This page helps you test the WooCommerce API connection and troubleshoot any issues.</p>
      
      <TestButton onClick={testAPI} disabled={loading}>
        {loading ? 'Testing...' : 'Test WooCommerce API'}
      </TestButton>
      
      {error && <ErrorContainer>{error}</ErrorContainer>}
      
      {result && (
        <ResultContainer>
          {result}
        </ResultContainer>
      )}
    </TestContainer>
  );
};

export default WooCommerceTest;